import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';

import TripSummaryPanel from './components/TripSummaryPanel';
import DayItineraryCard from './components/DayItineraryCard';
import ActionButtons from './components/ActionButtons';
import ModifyTripModal from './components/ModifyTripModal';
import EmailPDFModal from './components/EmailPDFModal';
import DateSelectionModal from './components/DateSelectionModal';

import SEOHead from '../../components/SEOHead';
import { tripService } from '../../services/tripService';


import { emailService } from '../../services/emailService';
import Icon from '../../components/AppIcon';


const TripConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  
  // Get real trip data from location state - AI-generated data from the chat interface
  const tripData = location?.state?.tripData;
  
  // ✅ CRITICAL FIX: Initialize expandedDays dynamically based on actual number of days in itinerary
  const [expandedDays, setExpandedDays] = useState(() => {
    if (!tripData?.itinerary) return { 1: true };
    
    // Create initial state with first day expanded, rest collapsed
    const initialState = {};
    for (let i = 0; i < tripData?.itinerary?.length; i++) {
      initialState[i + 1] = i === 0; // Only first day (index 0) is expanded
    }
    return initialState;
  });
  
  const [acknowledgments, setAcknowledgments] = useState({
    parkRegulations: false,
    weatherConditions: false,
    reservationRequirements: false,
    safetyResponsibility: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [tripId, setTripId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSavingTrip, setIsSavingTrip] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showEmailPDFModal, setShowEmailPDFModal] = useState(false);
  const [showDateSelectionModal, setShowDateSelectionModal] = useState(false);
  const [dateSelectionLoading, setDateSelectionLoading] = useState(false);
  const [emailPDFLoading, setEmailPDFLoading] = useState(false);
  const [emailPDFSuccess, setEmailPDFSuccess] = useState(false);
  const [emailPDFError, setEmailPDFError] = useState('');
  const [sentEmailAddress, setSentEmailAddress] = useState('');

  // ✅ CRITICAL FIX: Update expandedDays when tripData changes
  useEffect(() => {
    if (tripData?.itinerary?.length > 0) {
      const newExpandedState = {};
      for (let i = 0; i < tripData?.itinerary?.length; i++) {
        // Default to collapsed except first day (don't reference previous state to avoid loop)
        newExpandedState[i + 1] = i === 0;
      }
      setExpandedDays(newExpandedState);
    }
  }, [tripData?.itinerary?.length]); // Removed expandedDays dependency to prevent infinite loop

  // Redirect if no trip data is provided
  useEffect(() => {
    if (!tripData) {
      navigate('/ai-trip-planning-interface');
    }
  }, [tripData, navigate]);

  // ✅ CRITICAL FIX: Auto-save trip WITH ITINERARY as draft when component mounts
  useEffect(() => {
    const autoSaveTripAsDraft = async () => {
      // Only auto-save if:
      // 1. User is authenticated
      // 2. Trip data exists
      // 3. Trip hasn't been saved yet (tripId is null)
      // 4. Not already in the process of saving
      if (!isAuthenticated || !tripData || tripId || isSavingTrip) {
        return;
      }

      try {
        setIsSavingTrip(true);
        console.log('🔄 Auto-saving trip WITH ITINERARY as draft...');

        // ✅ CRITICAL FIX: Helper function to convert 12-hour time to 24-hour format
        const convertTo24Hour = (timeStr) => {
          if (!timeStr) return '09:00:00';
          
          // If already in 24-hour format (no AM/PM), return as is
          if (!timeStr?.match(/AM|PM/i)) {
            return timeStr?.includes(':') ? `${timeStr}:00` : '09:00:00';
          }
          
          // Parse 12-hour format
          const match = timeStr?.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!match) return '09:00:00';
          
          let hours = parseInt(match?.[1]);
          const minutes = match?.[2];
          const period = match?.[3]?.toUpperCase();
          
          // Convert to 24-hour format
          if (period === 'PM' && hours !== 12) {
            hours += 12;
          } else if (period === 'AM' && hours === 12) {
            hours = 0;
          }
          
          return `${String(hours)?.padStart(2, '0')}:${minutes}:00`;
        };

        // ✅ CRITICAL FIX: Extract itinerary items from AI trip data with proper activity handling
        const itineraryItems = [];
        
        // Process each day in the itinerary
        (tripData?.itinerary || [])?.forEach((day, dayIndex) => {
          const dayNumber = dayIndex + 1;
          
          // ✅ CRITICAL FIX: Process EACH activity as a separate itinerary item
          if (Array.isArray(day?.activities)) {
            day?.activities?.forEach((activity, activityIndex) => {
              // Build comprehensive description from activity details
              let fullDescription = '';
              
              // If activity is a string, use it directly
              if (typeof activity === 'string') {
                fullDescription = activity;
              } else {
                // Extract all activity details
                const activityTitle = activity?.title || activity?.name || `Activity ${activityIndex + 1}`;
                const activityDescription = activity?.description || activity?.details || '';
                const activityLocation = activity?.location || '';
                const activityDistance = activity?.distance || '';
                const activityDifficulty = activity?.difficulty || '';
                const activityCost = activity?.cost || '';
                
                // Build complete description with all details
                fullDescription = activityTitle;
                if (activityDescription) {
                  fullDescription += `\n\n${activityDescription}`;
                }
                if (activityLocation) {
                  fullDescription += `\n\nLocation: ${activityLocation}`;
                }
                if (activityDistance) {
                  fullDescription += ` (${activityDistance})`;
                }
                if (activityDifficulty) {
                  fullDescription += `\nDifficulty: ${activityDifficulty}`;
                }
                if (activityCost) {
                  fullDescription += `\nCost: ${activityCost}`;
                }
              }
              
              // ✅ CRITICAL FIX: Properly convert 12-hour time to 24-hour format
              const startTime = typeof activity === 'object' 
                ? convertTo24Hour(activity?.time) 
                : '09:00:00';
              
              const endTime = typeof activity === 'object' ? convertTo24Hour(activity?.endTime) || '17:00:00' : '17:00:00';
              
              // Create itinerary item for this specific activity
              itineraryItems?.push({
                dayNumber: dayNumber,
                title: typeof activity === 'string' ? activity : (activity?.title || activity?.name || `Activity ${activityIndex + 1}`),
                description: fullDescription,
                startTime: startTime,
                endTime: endTime,
                location: typeof activity === 'object' ? (activity?.location || tripData?.featuredParks?.[0] || 'State Park') : (tripData?.featuredParks?.[0] || 'State Park'),
                activityType: typeof activity === 'object' ? (activity?.type || 'Sightseeing') : 'Sightseeing',
                cost: typeof activity === 'object' ? (parseFloat(activity?.cost) || 0) : 0,
                notes: typeof activity === 'object' ? (activity?.notes || '') : ''
              });
            });
          } else if (typeof day?.activities === 'string') {
            // If activities is a string, create single item for the day
            itineraryItems?.push({
              dayNumber: dayNumber,
              title: day?.title || `Day ${dayNumber}`,
              description: day?.activities,
              startTime: day?.startTime || '09:00:00',
              endTime: day?.endTime || '17:00:00',
              location: day?.location || tripData?.featuredParks?.[0] || 'State Park',
              activityType: day?.activityType || 'Sightseeing',
              cost: day?.cost || 0,
              notes: day?.notes || ''
            });
          }
        });

        console.log(`✅ Extracted ${itineraryItems?.length} itinerary items for saving`);

        // Prepare trip data for saving
        const draftTripData = {
          destination: tripData?.featuredParks?.[0] || 'AI-Generated Adventure',
          park: tripData?.featuredParks?.join(', ') || 'Multiple State Parks',
          description: `${tripData?.duration || '3-day'} adventure featuring ${tripData?.activityTypes?.join(', ') || 'outdoor activities'}`,
          startDate: tripData?.itinerary?.[0]?.date ? new Date(tripData?.itinerary?.[0]?.date)?.toISOString()?.split('T')?.[0] : null,
          endDate: tripData?.itinerary?.[tripData?.itinerary?.length - 1]?.date ? new Date(tripData?.itinerary?.[tripData?.itinerary?.length - 1]?.date)?.toISOString()?.split('T')?.[0] : null,
          timeframeText: tripData?.duration || null,
          travelers: 2,
          status: 'draft',
          imageUrl: tripData?.itinerary?.[0]?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e',
          imageAlt: tripData?.itinerary?.[0]?.parkImageAlt || 'Beautiful state park landscape',
          totalCost: parseFloat(tripData?.estimatedCost?.match(/\d+/)?.[0]) || 500,
          activities: tripData?.activityTypes || ['Hiking', 'Camping', 'Nature Photography'],
          accommodation: tripData?.accommodations?.campgrounds?.[0]?.name || 'Camping',
          notes: `Weather forecast: ${tripData?.weatherForecast || 'Check local conditions'}. Auto-saved as draft.`,
          isPublic: false
        };

        // ✅ CRITICAL FIX: Use createTripWithItinerary to save trip AND itinerary together
        const savedTrip = await tripService?.createTripWithItinerary(
          draftTripData,
          itineraryItems,
          tripData // Pass original trip data as ai_trip_data
        );
        
        // Store trip ID for later use
        setTripId(savedTrip?.id);
        
        console.log('✅ Trip auto-saved with ID:', savedTrip?.id);
        console.log('✅ Itinerary items saved:', savedTrip?.itinerary?.length);
      } catch (err) {
        console.error('❌ Failed to auto-save trip with itinerary:', err);
        // Don't show error to user - this is background operation
        // Trip can still be manually saved later
      } finally {
        setIsSavingTrip(false);
      }
    };

    autoSaveTripAsDraft();
  }, [isAuthenticated, tripData, tripId, isSavingTrip]);

  const toggleDayExpansion = (dayNumber) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayNumber]: !prev?.[dayNumber]
    }));
  };

  const handleAcknowledgmentChange = (type, checked) => {
    setAcknowledgments((prev) => ({
      ...prev,
      [type]: checked
    }));
  };

  const allAcknowledgmentsChecked = Object.values(acknowledgments)?.every(Boolean);

  const handleConfirmTrip = async () => {
    // Check if user is authenticated before confirming
    if (!isAuthenticated) {
      setShowAccountPrompt(true);
      return;
    }

    // ✅ CRITICAL FIX: For logged-in users, ALWAYS show date selection modal first
    // This ensures users explicitly confirm their trip dates before saving
    console.log('🔍 Logged-in user confirming trip - showing date selection modal');
    setShowDateSelectionModal(true);
    return;
  };

  // ✅ FIXED: Handle date selection confirmation without recursive loop
  const handleDateSelectionConfirm = async (dates) => {
    try {
      setDateSelectionLoading(true);
      setError('');

      // If trip exists, update it with dates
      if (tripId) {
        await tripService?.updateTrip(tripId, {
          startDate: dates?.startDate,
          endDate: dates?.endDate,
          status: 'upcoming' // ✅ FIXED: Set status to upcoming directly here
        });

        setShowDateSelectionModal(false);
        setMessage({ type: 'success', text: 'Trip confirmed with dates successfully!' });
        
        // ✅ FIXED: Navigate directly without calling handleConfirmTrip again
        setTimeout(() => {
          navigate(`/trip-detail-view/${tripId}`, {
            state: {
              isNewTrip: true,
              showConfirmationMessage: true
            }
          });
        }, 2000);
      } else {
        // Create trip with dates included
        // ✅ CRITICAL FIX: Helper function to convert 12-hour time to 24-hour format
        const convertTo24Hour = (timeStr) => {
          if (!timeStr) return '09:00:00';
          
          // If already in 24-hour format (no AM/PM), return as is
          if (!timeStr?.match(/AM|PM/i)) {
            return timeStr?.includes(':') ? `${timeStr}:00` : '09:00:00';
          }
          
          // Parse 12-hour format
          const match = timeStr?.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!match) return '09:00:00';
          
          let hours = parseInt(match?.[1]);
          const minutes = match?.[2];
          const period = match?.[3]?.toUpperCase();
          
          // Convert to 24-hour format
          if (period === 'PM' && hours !== 12) {
            hours += 12;
          } else if (period === 'AM' && hours === 12) {
            hours = 0;
          }
          
          return `${String(hours)?.padStart(2, '0')}:${minutes}:00`;
        };

        const itineraryItems = [];
        
        // Process each day in the itinerary
        (tripData?.itinerary || [])?.forEach((day, dayIndex) => {
          const dayNumber = dayIndex + 1;
          
          // ✅ CRITICAL FIX: Process EACH activity as a separate itinerary item
          if (Array.isArray(day?.activities)) {
            day?.activities?.forEach((activity, activityIndex) => {
              // Build comprehensive description from activity details
              let fullDescription = '';
              
              // If activity is a string, use it directly
              if (typeof activity === 'string') {
                fullDescription = activity;
              } else {
                // Extract all activity details
                const activityTitle = activity?.title || activity?.name || `Activity ${activityIndex + 1}`;
                const activityDescription = activity?.description || activity?.details || '';
                const activityLocation = activity?.location || '';
                const activityDistance = activity?.distance || '';
                const activityDifficulty = activity?.difficulty || '';
                const activityCost = activity?.cost || '';
                
                // Build complete description with all details
                fullDescription = activityTitle;
                if (activityDescription) {
                  fullDescription += `\n\n${activityDescription}`;
                }
                if (activityLocation) {
                  fullDescription += `\n\nLocation: ${activityLocation}`;
                }
                if (activityDistance) {
                  fullDescription += ` (${activityDistance})`;
                }
                if (activityDifficulty) {
                  fullDescription += `\nDifficulty: ${activityDifficulty}`;
                }
                if (activityCost) {
                  fullDescription += `\nCost: ${activityCost}`;
                }
              }
              
              // ✅ CRITICAL FIX: Properly convert 12-hour time to 24-hour format
              const startTime = typeof activity === 'object' 
                ? convertTo24Hour(activity?.time) 
                : '09:00:00';
              
              const endTime = typeof activity === 'object' ? convertTo24Hour(activity?.endTime) || '17:00:00' : '17:00:00';
              
              // Create itinerary item for this specific activity
              itineraryItems?.push({
                dayNumber: dayNumber,
                title: typeof activity === 'string' ? activity : (activity?.title || activity?.name || `Activity ${activityIndex + 1}`),
                description: fullDescription,
                startTime: startTime,
                endTime: endTime,
                location: typeof activity === 'object' ? (activity?.location || tripData?.featuredParks?.[0] || 'State Park') : (tripData?.featuredParks?.[0] || 'State Park'),
                activityType: typeof activity === 'object' ? (activity?.type || 'Sightseeing') : 'Sightseeing',
                cost: typeof activity === 'object' ? (parseFloat(activity?.cost) || 0) : 0,
                notes: typeof activity === 'object' ? (activity?.notes || '') : ''
              });
            });
          } else if (typeof day?.activities === 'string') {
            // If activities is a string, create single item for the day
            itineraryItems?.push({
              dayNumber: dayNumber,
              title: day?.title || `Day ${dayNumber}`,
              description: day?.activities,
              startTime: day?.startTime || '09:00:00',
              endTime: day?.endTime || '17:00:00',
              location: day?.location || tripData?.featuredParks?.[0] || 'State Park',
              activityType: day?.activityType || 'Sightseeing',
              cost: day?.cost || 0,
              notes: day?.notes || ''
            });
          }
        });

        const draftTripData = {
          destination: tripData?.featuredParks?.[0] || 'AI-Generated Adventure',
          park: tripData?.featuredParks?.join(', ') || 'Multiple State Parks',
          description: `${tripData?.duration || '3-day'} adventure featuring ${tripData?.activityTypes?.join(', ') || 'outdoor activities'}`,
          startDate: dates?.startDate, // Use selected dates
          endDate: dates?.endDate, // Use selected dates
          timeframeText: tripData?.duration || null,
          travelers: 2,
          status: 'upcoming',
          imageUrl: tripData?.itinerary?.[0]?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e',
          imageAlt: tripData?.itinerary?.[0]?.parkImageAlt || 'Beautiful state park landscape',
          totalCost: parseFloat(tripData?.estimatedCost?.match(/\d+/)?.[0]) || 500,
          activities: tripData?.activityTypes || ['Hiking', 'Camping', 'Nature Photography'],
          accommodation: tripData?.accommodations?.campgrounds?.[0]?.name || 'Camping',
          notes: `Weather forecast: ${tripData?.weatherForecast || 'Check local conditions'}.`,
          isPublic: false
        };

        const savedTrip = await tripService?.createTripWithItinerary(
          draftTripData,
          itineraryItems,
          tripData
        );
        
        setTripId(savedTrip?.id);
        setShowDateSelectionModal(false);
        setMessage({ type: 'success', text: 'Trip confirmed with dates successfully!' });
        
        setTimeout(() => {
          navigate(`/trip-detail-view/${savedTrip?.id}`, {
            state: {
              isNewTrip: true,
              tripData: savedTrip,
              showConfirmationMessage: true
            }
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving trip dates:', error);
      setError(error?.message || 'Failed to save trip dates. Please try again.');
    } finally {
      setDateSelectionLoading(false);
    }
  };

  const handleEmailPDF = () => {
    setShowAccountPrompt(false);
    setShowEmailModal(true);
    setEmailSuccess(false);
    setEmailAddress('');
  };

  const handleSendEmailPDF = async () => {
    if (!emailAddress?.trim()) {
      setError('Please enter a valid email address.');
      return;
    }

    setEmailLoading(true);
    setError('');

    try {
      // Send PDF via email using the email service
      const result = await emailService?.sendTripPDF(tripData, emailAddress?.trim());
      
      if (result?.success) {
        setEmailSuccess(true);
        
        // If it's a fallback (direct download), show different message
        if (result?.fallback) {
          setError(result?.message); // This is actually a success message in this case
        }
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err?.message || 'Failed to send email. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCreateAccount = () => {
    setShowAccountPrompt(false);
    navigate('/user-registration', {
      state: { 
        returnTo: '/trip-confirmation',
        tripData: tripData,
        fromTripConfirmation: true
      }
    });
  };

  const handleModifyRequest = () => {
    setShowModifyModal(true);
  };

  const handleModifyConfirm = (modificationData) => {
    setShowModifyModal(false);

    // Navigate to AI trip planning page with modification context
    navigate('/ai-trip-planning-interface', {
      state: {
        modifyExisting: true,
        currentTripData: tripData,
        modificationRequest: modificationData?.request,
        modificationCategory: modificationData?.category
      }
    });
  };

  const handleSaveForLater = async () => {
    if (!isAuthenticated) {
      // Redirect to login
      navigate('/user-login', {
        state: { 
          returnTo: '/trip-confirmation',
          tripData: tripData 
        }
      });
      return;
    }

    // ✅ IMPROVED: Check if trip is already saved
    if (tripId) {
      // Trip already exists, navigate to trip detail view
      setMessage({ type: 'success', text: 'Trip already saved! Opening trip details...' });
      setTimeout(() => {
        navigate(`/trip-detail-view/${tripId}`, {
          state: {
            isNewTrip: true,
            showConfirmationMessage: true
          }
        });
      }, 2000);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // ✅ CRITICAL FIX: Helper function to convert 12-hour time to 24-hour format
      const convertTo24Hour = (timeStr) => {
        if (!timeStr) return '09:00:00';
        
        // If already in 24-hour format (no AM/PM), return as is
        if (!timeStr?.match(/AM|PM/i)) {
          return timeStr?.includes(':') ? `${timeStr}:00` : '09:00:00';
        }
        
        // Parse 12-hour format
        const match = timeStr?.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return '09:00:00';
        
        let hours = parseInt(match?.[1]);
        const minutes = match?.[2];
        const period = match?.[3]?.toUpperCase();
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return `${String(hours)?.padStart(2, '0')}:${minutes}:00`;
      };
      
      // ✅ CRITICAL FIX: Extract itinerary items with proper activity handling
      const itineraryItems = [];
      
      // Process each day in the itinerary
      (tripData?.itinerary || [])?.forEach((day, dayIndex) => {
        const dayNumber = dayIndex + 1;
        
        // ✅ CRITICAL FIX: Process EACH activity as a separate itinerary item
        if (Array.isArray(day?.activities)) {
          day?.activities?.forEach((activity, activityIndex) => {
            // Build comprehensive description from activity details
            let fullDescription = '';
            
            // If activity is a string, use it directly
            if (typeof activity === 'string') {
              fullDescription = activity;
            } else {
              // Extract all activity details
              const activityTitle = activity?.title || activity?.name || `Activity ${activityIndex + 1}`;
              const activityDescription = activity?.description || activity?.details || '';
              const activityLocation = activity?.location || '';
              const activityDistance = activity?.distance || '';
              const activityDifficulty = activity?.difficulty || '';
              const activityCost = activity?.cost || '';
              
              // Build complete description with all details
              fullDescription = activityTitle;
              if (activityDescription) {
                fullDescription += `\n\n${activityDescription}`;
              }
              if (activityLocation) {
                fullDescription += `\n\nLocation: ${activityLocation}`;
              }
              if (activityDistance) {
                fullDescription += ` (${activityDistance})`;
              }
              if (activityDifficulty) {
                fullDescription += `\nDifficulty: ${activityDifficulty}`;
              }
              if (activityCost) {
                fullDescription += `\nCost: ${activityCost}`;
              }
            }
            
            // ✅ CRITICAL FIX: Properly convert 12-hour time to 24-hour format
            const startTime = typeof activity === 'object' 
              ? convertTo24Hour(activity?.time) 
              : '09:00:00';
            
            const endTime = typeof activity === 'object' ? convertTo24Hour(activity?.endTime) || '17:00:00' : '17:00:00';
            
            // Create itinerary item for this specific activity
            itineraryItems?.push({
              dayNumber: dayNumber,
              title: typeof activity === 'string' ? activity : (activity?.title || activity?.name || `Activity ${activityIndex + 1}`),
              description: fullDescription,
              startTime: startTime,
              endTime: endTime,
              location: typeof activity === 'object' ? (activity?.location || tripData?.featuredParks?.[0] || 'State Park') : (tripData?.featuredParks?.[0] || 'State Park'),
              activityType: typeof activity === 'object' ? (activity?.type || 'Sightseeing') : 'Sightseeing',
              cost: typeof activity === 'object' ? (parseFloat(activity?.cost) || 0) : 0,
              notes: typeof activity === 'object' ? (activity?.notes || '') : ''
            });
          });
        } else if (typeof day?.activities === 'string') {
          // If activities is a string, create single item for the day
          itineraryItems?.push({
            dayNumber: dayNumber,
            title: day?.title || `Day ${dayNumber}`,
            description: day?.activities,
            startTime: day?.startTime || '09:00:00',
            endTime: day?.endTime || '17:00:00',
            location: day?.location || tripData?.featuredParks?.[0] || 'State Park',
            activityType: day?.activityType || 'Sightseeing',
            cost: day?.cost || 0,
            notes: day?.notes || ''
          });
        }
      });

      // Save as draft using real AI data
      const draftTripData = {
        destination: tripData?.featuredParks?.[0] || 'AI-Generated Adventure',
        park: tripData?.featuredParks?.join(', ') || 'Multiple State Parks',
        description: `${tripData?.duration || '3-day'} adventure featuring ${tripData?.activityTypes?.join(', ') || 'outdoor activities'}`,
        startDate: tripData?.itinerary?.[0]?.date ? new Date(tripData?.itinerary?.[0]?.date)?.toISOString()?.split('T')?.[0] : null,
        endDate: tripData?.itinerary?.[tripData?.itinerary?.length - 1]?.date ? new Date(tripData?.itinerary?.[tripData?.itinerary?.length - 1]?.date)?.toISOString()?.split('T')?.[0] : null,
        timeframeText: tripData?.duration || null,
        travelers: 2,
        status: 'draft',
        imageUrl: tripData?.itinerary?.[0]?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e',
        imageAlt: tripData?.itinerary?.[0]?.parkImageAlt || 'Beautiful state park landscape',
        totalCost: parseFloat(tripData?.estimatedCost?.match(/\d+/)?.[0]) || 500,
        activities: tripData?.activityTypes || ['Hiking', 'Camping', 'Nature Photography'],
        accommodation: tripData?.accommodations?.campgrounds?.[0]?.name || 'Camping',
        notes: `Weather forecast: ${tripData?.weatherForecast || 'Check local conditions'}. Saved for later.`,
        isPublic: false
      };

      // ✅ CRITICAL FIX: Use createTripWithItinerary to save trip AND itinerary
      const savedTrip = await tripService?.createTripWithItinerary(
        draftTripData,
        itineraryItems,
        tripData
      );
      setTripId(savedTrip?.id); // ✅ Store trip ID after saving
      
      // Show success and redirect to trip detail view
      setMessage({ type: 'success', text: 'Trip saved as draft! Opening trip details...' });
      setTimeout(() => {
        navigate(`/trip-detail-view/${savedTrip?.id}`, {
          state: {
            isNewTrip: true,
            showConfirmationMessage: true
          }
        });
      }, 2000);

    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err?.message || 'Failed to save trip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareTrip = () => {
    // Show "Feature coming soon" toast message
    setMessage({ 
      type: 'info', 
      text: '🚀 Feature coming soon! We are working on trip sharing functionality.' 
    });

    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({ type: '', text: '' });
    }, 3000);
  };

  const handleCloseEmailPDFModal = () => {
    setShowEmailPDFModal(false);
    setEmailPDFError('');
    setEmailPDFSuccess(false);
    setSentEmailAddress('');
  };

  const handleSendEmailPDFModal = async (email) => {
    if (!email?.trim()) {
      setEmailPDFError('Please enter a valid email address.');
      return;
    }

    setEmailPDFLoading(true);
    setEmailPDFError('');

    try {
      // Send PDF via email using the email service
      const result = await emailService?.sendTripPDF(tripData, email?.trim());
      
      if (result?.success) {
        setEmailPDFSuccess(true);
        setSentEmailAddress(email?.trim());
        
        // If it's a fallback (direct download), show different message
        if (result?.fallback) {
          setEmailPDFError(result?.message); // This is actually a success message in this case
        }
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailPDFError(err?.message || 'Failed to send email. Please try again.');
    } finally {
      setEmailPDFLoading(false);
    }
  };

  const handleSignIn = () => {
    setShowAccountPrompt(false);
    navigate('/user-login', {
      state: { 
        returnTo: '/trip-confirmation',
        tripData: tripData,
        autoConfirmTrip: true  // NEW: Flag to auto-confirm after login
      }
    });
  };

  // Show loading while auth context is initializing
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="mx-auto mb-4 text-muted-foreground animate-spin" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            Loading...
          </h2>
          <p className="text-muted-foreground">
            Checking authentication status
          </p>
        </div>
      </div>
    );
  }

  // Show loading or redirect message if no trip data
  if (!tripData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="MapPin" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            No Trip Data Found
          </h2>
          <p className="text-muted-foreground mb-4">
            Please start by creating a trip plan with our AI assistant.
          </p>
          <button
            onClick={() => navigate('/ai-trip-planning-interface')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic"
          >
            Start Planning Trip
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        pageType="tripConfirmation"
        title="Confirm Your Trip - Save Your Adventure | ParkTrip AI"
        description="Review and save your AI-generated state park itinerary. Customize your trip and access it anytime from your dashboard."
        canonicalUrl="https://parktripplannerai.com/trip-confirmation"
        noindex={true}
        keywords="trip confirmation, save itinerary, state park planning, AI trip planner"
        ogImage="https://images.unsplash.com/photo-1441974231531-c6227db76b6e"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TravelAction",
          "name": "Trip Confirmation"
        }}
      />
      <div className="min-h-screen bg-background">
        <Header 
          propIsAuthenticated={isAuthenticated}
          propUser={user}
          propOnLogout={() => navigate('/user-login')}
        />

        {/* Success/Info Message Toast */}
        {message?.text && (
          <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full px-4`}>
            <div className={`p-4 rounded-organic shadow-organic-lg ${
              message?.type === 'success' ? 'bg-success text-success-foreground' 
                : message?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'
            }`}>
              <p className="font-body text-sm">{message?.text}</p>
            </div>
          </div>
        )}

        {/* Modify Trip Modal */}
        <ModifyTripModal
          isOpen={showModifyModal}
          onClose={() => setShowModifyModal(false)}
          onConfirm={handleModifyConfirm}
          tripData={tripData}
        />

        {/* Email PDF Modal */}
        <EmailPDFModal
          isOpen={showEmailPDFModal}
          onClose={handleCloseEmailPDFModal}
          onSend={handleSendEmailPDFModal}
          loading={emailPDFLoading}
          success={emailPDFSuccess}
          error={emailPDFError}
          sentEmail={sentEmailAddress}
        />

        {/* ✅ NEW: Date Selection Modal */}
        <DateSelectionModal
          isOpen={showDateSelectionModal}
          onClose={() => setShowDateSelectionModal(false)}
          onConfirm={handleDateSelectionConfirm}
          loading={dateSelectionLoading}
          tripData={tripData}
        />

        {/* **NEW**: Account Prompt Modal */}
        {showAccountPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-organic max-w-md w-full p-6 shadow-organic-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-[#2C5F2D] rounded-organic flex items-center justify-center">
                  <Icon name="Save" size={24} color="white" />
                </div>
                <div>
                  <h3 className="font-heading font-heading-bold text-xl text-card-foreground">
                    Save Your Trip Plan
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Choose how you'd like to keep your itinerary
                  </p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                {/* Sign In Option - NEW */}
                <button
                  onClick={handleSignIn}
                  className="w-full flex items-center justify-between p-4 bg-[#2C5F2D] hover:bg-[#234A24] rounded-organic transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-organic flex items-center justify-center">
                      <Icon name="LogIn" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <div className="font-body font-body-medium text-white">Sign In & Save</div>
                      <div className="text-xs text-white/80">Already have an account? Sign in to save</div>
                    </div>
                  </div>
                  <Icon name="ArrowRight" size={16} color="white" className="group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Create Account Option - Updated Styling */}
                <button
                  onClick={handleCreateAccount}
                  className="w-full flex items-center justify-between p-4 bg-[#97C086] hover:bg-[#7DA76E] rounded-organic transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/30 rounded-organic flex items-center justify-center">
                      <Icon name="UserPlus" size={20} color="white" />
                    </div>
                    <div className="text-left">
                      <div className="font-body font-body-medium text-white">Create Account & Save</div>
                      <div className="text-xs text-white/90">New here? Save trip and get recommendations</div>
                    </div>
                  </div>
                  <Icon name="ArrowRight" size={16} color="white" className="group-hover:translate-x-1 transition-transform" />
                </button>
                
                {/* Email PDF Option - Updated Styling */}
                <button
                  onClick={handleEmailPDF}
                  className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-organic transition-colors border border-border group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-organic flex items-center justify-center">
                      <Icon name="Mail" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="text-left">
                      <div className="font-body font-body-medium text-card-foreground">Email PDF Summary</div>
                      <div className="text-xs text-muted-foreground">Get a PDF copy sent to your email</div>
                    </div>
                  </div>
                  <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              <button
                onClick={() => setShowAccountPrompt(false)}
                className="w-full py-2 text-sm text-muted-foreground hover:text-card-foreground transition-colors font-body"
              >
                Maybe Later
              </button>
            </div>
          </div>
        )}
        {/* **NEW**: Email Modal with Success State */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-organic max-w-md w-full p-6 shadow-organic-lg">
              {!emailSuccess ? (
                // Email Form
                (<>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-heading-bold text-lg text-card-foreground">
                      Email Trip PDF
                    </h3>
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="p-1 hover:bg-muted rounded-organic-sm transition-colors"
                    >
                      <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-body font-body-medium text-card-foreground mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e?.target?.value)}
                        placeholder="Enter your email address"
                        className="w-full px-3 py-2 border border-border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        disabled={emailLoading}
                      />
                    </div>
                    
                    <div className="bg-muted/30 rounded-organic p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="FileText" size={16} color="var(--color-primary)" />
                        <span className="text-sm font-body font-body-medium text-card-foreground">
                          PDF will include:
                        </span>
                      </div>
                      <ul className="text-xs text-muted-foreground space-y-1 ml-6">
                        <li>• Complete day-by-day itinerary</li>
                        <li>• Park information and maps</li>
                        <li>• Activity recommendations</li>
                        <li>• Weather forecast and packing tips</li>
                      </ul>
                    </div>
                    
                    <button
                      onClick={handleSendEmailPDF}
                      disabled={emailLoading || !emailAddress?.trim()}
                      className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-organic hover:bg-primary/90 transition-colors font-body font-body-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {emailLoading && <Icon name="Loader" size={16} className="animate-spin" />}
                      <span>{emailLoading ? 'Sending...' : 'Send PDF via Email'}</span>
                    </button>
                  </div>
                </>)
              ) : (
                // Success State
                (<>
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-success rounded-organic flex items-center justify-center">
                        <Icon name="CheckCircle" size={32} color="white" />
                      </div>
                    </div>
                    <h3 className="font-heading font-heading-bold text-xl text-card-foreground mb-2">
                      Successfully Sent!
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Check your inbox at <strong>{emailAddress}</strong> for your trip PDF.
                    </p>
                    <div className="bg-muted/30 rounded-organic p-3 mb-4">
                      <p className="text-xs text-muted-foreground">
                        💡 <strong>Tip:</strong> Check your spam folder if you don't see the email within a few minutes.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-organic hover:bg-primary/90 transition-colors font-body font-body-medium"
                    >
                      Close
                    </button>
                  </div>
                </>)
              )}
            </div>
          </div>
        )}
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <button
                  onClick={() => navigate('/ai-trip-planning-interface')}
                  className="p-2 hover:bg-muted rounded-organic-sm transition-organic">
                  <Icon name="ArrowLeft" size={20} color="var(--color-muted-foreground)" />
                </button>
                <div>
                  <h1 className="font-heading font-heading-bold text-3xl text-foreground">Confirm Your Trip</h1>
                  <p className="font-body text-muted-foreground mt-1">
                    Review your AI-generated itinerary and finalize your state park adventure
                  </p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-organic">
                <p className="font-body text-destructive text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content - Itinerary */}
              <div className="lg:col-span-2 space-y-6">
                {/* Trip Overview */}
                <div className="bg-card border border-border rounded-organic p-6 shadow-organic">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-organic flex items-center justify-center">
                      <Icon name="MapPin" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h2 className="font-heading font-heading-bold text-xl text-card-foreground">
                        {tripData?.featuredParks?.[0] || 'State Park'} Adventure
                      </h2>
                      <p className="font-body text-muted-foreground">
                        {tripData?.duration || 'Multi-day'} wilderness exploration
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="text-center p-3 bg-muted/30 rounded-organic">
                      <Icon name="Calendar" size={20} color="var(--color-primary)" className="mx-auto mb-1" />
                      <p className="font-caption text-xs text-muted-foreground">Duration</p>
                      <p className="font-body font-body-medium text-sm text-card-foreground">
                        {tripData?.duration?.split(',')?.[0] || '3 Days'}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-organic">
                      <Icon name="Users" size={20} color="var(--color-success)" className="mx-auto mb-1" />
                      <p className="font-caption text-xs text-muted-foreground">Group Size</p>
                      <p className="font-body font-body-medium text-sm text-card-foreground">2-4 People</p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-organic">
                      <Icon name="Activity" size={20} color="var(--color-accent)" className="mx-auto mb-1" />
                      <p className="font-caption text-xs text-muted-foreground">Activities</p>
                      <p className="font-body font-body-medium text-sm text-card-foreground">
                        {tripData?.activityTypes?.length || 0} Types
                      </p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-organic">
                      <Icon name="DollarSign" size={20} color="var(--color-secondary)" className="mx-auto mb-1" />
                      <p className="font-caption text-xs text-muted-foreground">Estimated Cost</p>
                      <p className="font-body font-body-medium text-sm text-card-foreground">
                        {tripData?.estimatedCost || 'Moderate'}
                      </p>
                    </div>
                  </div>

                  {/* **ENHANCED**: Featured Parks with Website Links */}
                  {tripData?.featuredParks?.length > 1 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-2">
                        Featured Parks:
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {tripData?.featuredParks?.map((park, index) => {
                          // Find corresponding park website
                          const parkWebsite = tripData?.reservationInfo?.officialParkWebsites?.find(site => 
                            site?.parkName?.toLowerCase()?.includes(park?.toLowerCase()?.split(' ')?.[0])
                          );
                          
                          return (
                            <div key={index} className="bg-primary/10 p-3 rounded-organic">
                              <div className="flex items-center justify-between">
                                <span className="text-primary text-sm font-medium">{park}</span>
                                {parkWebsite && (
                                  <div className="flex space-x-2">
                                    <a
                                      href={parkWebsite?.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                    >
                                      <Icon name="ExternalLink" size={12} />
                                      <span>Info</span>
                                    </a>
                                    {parkWebsite?.reservationLink && (
                                      <a
                                        href={parkWebsite?.reservationLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-1 text-success hover:underline text-xs"
                                      >
                                        <Icon name="Calendar" size={12} />
                                        <span>Reserve</span>
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                              {parkWebsite?.phone && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  📞 {parkWebsite?.phone}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* **ENHANCED**: Detailed Accommodation Information with Website Links */}
                  {tripData?.accommodations && (
                    <div className="mt-6">
                      <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-3">
                        Accommodation Options:
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Campgrounds */}
                        {tripData?.accommodations?.campgrounds?.length > 0 && (
                          <div className="p-4 bg-muted/20 rounded-organic">
                            <div className="flex items-center space-x-2 mb-2">
                              <Icon name="Tent" size={16} color="var(--color-green)" />
                              <h4 className="font-body font-body-medium text-sm text-card-foreground">Campgrounds</h4>
                            </div>
                            {tripData?.accommodations?.campgrounds?.slice(0, 2)?.map((campground, index) => (
                              <div key={index} className="mb-3">
                                <div className="flex items-center justify-between">
                                  <p className="font-caption text-xs font-medium text-card-foreground">{campground?.name}</p>
                                  {campground?.website && (
                                    <a
                                      href={campground?.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                    >
                                      <Icon name="ExternalLink" size={10} />
                                      <span>Details</span>
                                    </a>
                                  )}
                                </div>
                                <p className="font-caption text-xs text-muted-foreground">{campground?.location}</p>
                                <p className="font-caption text-xs text-success">{campground?.cost}</p>
                                {campground?.phone && (
                                  <p className="font-caption text-xs text-muted-foreground">📞 {campground?.phone}</p>
                                )}
                                {campground?.amenities?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {campground?.amenities?.slice(0, 3)?.map((amenity, aIndex) => (
                                      <span key={aIndex} className="px-1 py-0.5 bg-success/10 text-success text-xs rounded-organic-sm">
                                        {amenity}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Cabins */}
                        {tripData?.accommodations?.cabins?.length > 0 && (
                          <div className="p-4 bg-muted/20 rounded-organic">
                            <div className="flex items-center space-x-2 mb-2">
                              <Icon name="Home" size={16} color="var(--color-orange)" />
                              <h4 className="font-body font-body-medium text-sm text-card-foreground">Cabins & Lodges</h4>
                            </div>
                            {tripData?.accommodations?.cabins?.slice(0, 2)?.map((cabin, index) => (
                              <div key={index} className="mb-3">
                                <div className="flex items-center justify-between">
                                  <p className="font-caption text-xs font-medium text-card-foreground">{cabin?.name}</p>
                                  {cabin?.website && (
                                    <a
                                      href={cabin?.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                    >
                                      <Icon name="ExternalLink" size={10} />
                                      <span>Details</span>
                                    </a>
                                  )}
                                </div>
                                <p className="font-caption text-xs text-muted-foreground">Capacity: {cabin?.capacity}</p>
                                <p className="font-caption text-xs text-warning">{cabin?.cost}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Nearby Lodging with Website Links */}
                      {tripData?.accommodations?.nearbyLodging?.length > 0 && (
                        <div className="mt-4 p-3 bg-muted/20 rounded-organic">
                          <h4 className="font-body font-body-medium text-sm text-card-foreground mb-2">Nearby Lodging Options:</h4>
                          <div className="space-y-2">
                            {tripData?.accommodations?.nearbyLodging?.slice(0, 3)?.map((lodging, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <p className="font-caption text-xs text-muted-foreground">
                                  • {lodging?.name} - {lodging?.distance} ({lodging?.estimatedCost})
                                </p>
                                {lodging?.website && (
                                  <a
                                    href={lodging?.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                  >
                                    <Icon name="ExternalLink" size={10} />
                                    <span>View</span>
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* **NEW**: Dining Information */}
                  {tripData?.dining && (
                    <div className="mt-6">
                      <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-3">
                        Dining & Food Options:
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {tripData?.dining?.onSite?.length > 0 && (
                          <div className="p-3 bg-muted/20 rounded-organic">
                            <h4 className="font-caption text-xs font-medium text-card-foreground mb-1">On-Site Dining:</h4>
                            <div className="space-y-1">
                              {tripData?.dining?.onSite?.map((option, index) => (
                                <p key={index} className="font-caption text-xs text-muted-foreground">• {option}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {tripData?.dining?.nearby?.length > 0 && (
                          <div className="p-3 bg-muted/20 rounded-organic">
                            <h4 className="font-caption text-xs font-medium text-card-foreground mb-1">Nearby Restaurants:</h4>
                            <div className="space-y-1">
                              {tripData?.dining?.nearby?.slice(0, 3)?.map((option, index) => (
                                <p key={index} className="font-caption text-xs text-muted-foreground">• {option}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {tripData?.dining?.groceryStores?.length > 0 && (
                        <div className="mt-3 p-3 bg-muted/20 rounded-organic">
                          <h4 className="font-caption text-xs font-medium text-card-foreground mb-1">Grocery Stores:</h4>
                          <p className="font-caption text-xs text-muted-foreground">
                            {tripData?.dining?.groceryStores?.slice(0, 2)?.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activity Types */}
                  {tripData?.activityTypes?.length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-2">
                        Activities Included:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {tripData?.activityTypes?.map((activity, index) => (
                          <span
                            key={index}
                            className="bg-accent/10 text-accent text-xs px-2 py-1 rounded-organic"
                          >
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* **ENHANCED**: Reservation Information with Comprehensive Website Links */}
                  {tripData?.reservationInfo && (
                    <div className="mt-6 p-4 bg-primary/10 rounded-organic">
                      <div className="flex items-center space-x-2 mb-3">
                        <Icon name="Calendar" size={16} color="var(--color-primary)" />
                        <h3 className="font-heading font-heading-medium text-sm text-card-foreground">Reservation Information</h3>
                      </div>
                      
                      {/* Main Reservation System */}
                      {tripData?.reservationInfo?.website && (
                        <div className="mb-3 p-3 bg-white/50 rounded-organic">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-caption text-xs font-medium text-card-foreground">Main Reservation System</p>
                              <p className="font-caption text-xs text-muted-foreground">Centralized booking for multiple parks</p>
                            </div>
                            <a
                              href={tripData?.reservationInfo?.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 bg-primary text-primary-foreground px-2 py-1 rounded-organic text-xs hover:bg-primary/90 transition-colors"
                            >
                              <Icon name="ExternalLink" size={12} />
                              <span>Book Now</span>
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Individual Park Websites */}
                      {tripData?.reservationInfo?.officialParkWebsites?.length > 0 && (
                        <div className="space-y-2">
                          <p className="font-caption text-xs font-medium text-card-foreground">Individual Park Information:</p>
                          {tripData?.reservationInfo?.officialParkWebsites?.map((parkSite, index) => (
                            <div key={index} className="p-3 bg-white/30 rounded-organic">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-caption text-xs font-medium text-card-foreground">{parkSite?.parkName}</p>
                                  {parkSite?.phone && (
                                    <p className="font-caption text-xs text-muted-foreground">📞 {parkSite?.phone}</p>
                                  )}
                                </div>
                                <div className="flex space-x-2">
                                  <a
                                    href={parkSite?.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                  >
                                    <Icon name="Globe" size={12} />
                                    <span>Info</span>
                                  </a>
                                  {parkSite?.reservationLink && (
                                    <a
                                      href={parkSite?.reservationLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center space-x-1 bg-success text-success-foreground px-2 py-1 rounded-organic text-xs hover:bg-success/90 transition-colors"
                                    >
                                      <Icon name="Calendar" size={12} />
                                      <span>Reserve</span>
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* General Reservation Guidelines */}
                      <div className="mt-3 space-y-1">
                        {tripData?.reservationInfo?.advanceBooking && (
                          <p className="font-caption text-xs text-muted-foreground">
                            📅 Book {tripData?.reservationInfo?.advanceBooking} in advance
                          </p>
                        )}
                        {tripData?.reservationInfo?.phone && (
                          <p className="font-caption text-xs text-muted-foreground">
                            📞 General Info: {tripData?.reservationInfo?.phone}
                          </p>
                        )}
                        {tripData?.reservationInfo?.cancelPolicy && (
                          <p className="font-caption text-xs text-muted-foreground">
                            ℹ️ Cancellation: {tripData?.reservationInfo?.cancelPolicy}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Weather Forecast */}
                  {tripData?.weatherForecast && (
                    <div className="mt-4 p-3 bg-muted/30 rounded-organic">
                      <div className="flex items-center space-x-2">
                        <Icon name="Sun" size={16} color="var(--color-warning)" />
                        <span className="font-caption text-xs text-muted-foreground">Weather Forecast:</span>
                        <span className="font-body text-sm text-card-foreground">
                          {tripData?.weatherForecast}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Daily Itinerary */}
                <div className="space-y-4">
                  <h3 className="font-heading font-heading-bold text-xl text-foreground">Daily Itinerary</h3>
                  {tripData?.itinerary?.map((day, index) => (
                    <DayItineraryCard
                      key={index + 1}
                      day={day}
                      dayNumber={index + 1}
                      isExpanded={expandedDays?.[index + 1]}
                      onToggle={() => toggleDayExpansion(index + 1)}
                    />
                  )) || (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Calendar" size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No itinerary details available. Please regenerate your trip plan.</p>
                    </div>
                  )}
                </div>

                {/* Mobile Summary Panel */}
                <div className="lg:hidden">
                  <TripSummaryPanel tripData={tripData} />
                </div>

              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Desktop Summary Panel */}
                <div className="hidden lg:block">
                  <TripSummaryPanel tripData={tripData} />
                </div>

                {/* Enhanced Action Buttons with Acknowledgments */}
                <ActionButtons
                  onConfirmTrip={handleConfirmTrip}
                  onModifyRequest={handleModifyRequest}
                  onSaveForLater={handleSaveForLater}
                  onShareTrip={handleShareTrip}
                  onEmailPDF={handleEmailPDF}
                  isConfirmDisabled={!allAcknowledgmentsChecked}
                  isLoading={isLoading}
                  isAuthenticated={isAuthenticated}
                  acknowledgments={acknowledgments}
                  onAcknowledgmentChange={handleAcknowledgmentChange}
                />

                {/* Additional Info */}
                <div className="bg-card border border-border rounded-organic p-4 shadow-organic">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="Info" size={16} color="var(--color-primary)" />
                    <h4 className="font-body font-body-medium text-sm text-card-foreground">AI-Generated Trip</h4>
                  </div>
                  <p className="font-caption text-xs text-muted-foreground mb-3">
                    This itinerary was created by our AI assistant based on your preferences. 
                    You can modify it anytime by chatting with the AI or editing after confirmation.
                  </p>
                  <button 
                    onClick={handleModifyRequest}
                    className="font-caption text-xs text-primary hover:underline"
                  >
                    Request Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TripConfirmation;